"use client";

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';

const STORAGE_KEY = 'tvfoco_notification_prompt_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

function isForcedFromSearch(search) {
	try {
		const v = new URLSearchParams(search).get('showNotificationPrompt');
		if (v == null) return false;
		const n = String(v).trim().toLowerCase();
		return n === '1' || n === 'true' || n === 'yes';
	} catch {
		return false;
	}
}

const PushNotificationCard = () => {
	const [visible, setVisible] = useState(true);
	const [buttonDisabled, setButtonDisabled] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const forced = isForcedFromSearch(window.location.search || '');
		if (forced) {
			setVisible(true);
			return;
		}

		if ('Notification' in window && Notification.permission === 'granted') {
			setVisible(false);
			return;
		}

		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (raw) {
				const data = JSON.parse(raw);
				const dismissedAt =
					data && typeof data.dismissedAt === 'number' ? data.dismissedAt : null;
				if (dismissedAt != null && Date.now() - dismissedAt < TTL_MS) {
					setVisible(false);
					return;
				}
			}
		} catch {
			/* ignore */
		}

		setVisible(true);
	}, []);

	const persistDismiss = useCallback((extra = {}) => {
		try {
			const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify({ ...prev, dismissedAt: Date.now(), ...extra })
			);
		} catch {
			/* ignore */
		}
	}, []);

	const handleSubscribe = async () => {
		if (typeof window === 'undefined') return;

		if (!('Notification' in window)) {
			alert('Seu navegador não suporta notificações neste contexto.');
			return;
		}

		if (Notification.permission === 'denied') {
			alert('As notificações estão bloqueadas nas configurações do navegador.');
			persistDismiss({ lastPermissionResult: 'denied' });
			setVisible(false);
			return;
		}

		setButtonDisabled(true);
		try {
			const permission = await Notification.requestPermission();
			persistDismiss({ lastPermissionResult: permission });
			setVisible(false);
		} catch {
			setButtonDisabled(false);
		}
	};

	if (!visible) return null;

	return (
		<section
			className="push-notification-banner"
			role="region"
			aria-label="Convite para notificações"
			aria-live="polite"
		>
			<div className="push-notification-banner__inner">
				<div className="push-notification-banner__logo">
					<Image
						src="/logo-white.svg"
						alt="TV Foco"
						width={100}
						height={38}
						priority={false}
					/>
				</div>
				<p className="push-notification-banner__text">
					Quer receber avisos de notícias no seu dispositivo? Ative as notificações do TV Foco.
				</p>
				<button
					type="button"
					id="push-subscribe"
					className="push-notification-banner__btn"
					onClick={handleSubscribe}
					disabled={buttonDisabled}
					aria-label="Ativar notificações do TV Foco"
				>
					Ativar
				</button>
			</div>
		</section>
	);
};

export default PushNotificationCard;
